import {
  getWorkspaceAuthSession,
  isAccessTokenExpired,
} from '@amglobal/auth-shared'
import { useEffect } from 'react'
import { getMeSession } from '@/features/auth/api'
import { isAuthenticated } from '@/features/auth/session/workspace-session'
import { tryRefreshSession } from '@/shared/api/auth-interceptor'
import { forceSessionExpired } from '@/shared/auth/session-expired'
import { useHttpClient, type HttpClient } from '@/shared/api/http-client-context'

const REFRESH_BEFORE_EXPIRY_MS = 60_000

function readAccessTokenExpiryMs(token: string): number | null {
  try {
    const parts = token.split('.')
    if (parts.length < 2) return null
    const payload = JSON.parse(atob(parts[1] ?? '')) as { exp?: number }
    if (typeof payload.exp !== 'number') return null
    return payload.exp * 1000
  } catch {
    return null
  }
}

async function refreshIfNeeded(client: HttpClient): Promise<void> {
  const session = getWorkspaceAuthSession()
  if (!session || !isAccessTokenExpired(session.accessToken)) return
  const ok = await tryRefreshSession(client)
  if (!ok) forceSessionExpired()
}

/** After local DB flush, JWTs may look valid but UserSessions are gone — probe and relogin. */
async function ensureLiveSession(client: HttpClient): Promise<void> {
  const session = getWorkspaceAuthSession()
  if (!session) return

  if (isAccessTokenExpired(session.accessToken)) {
    const ok = await tryRefreshSession(client)
    if (!ok) forceSessionExpired()
    return
  }

  try {
    await getMeSession(client)
  } catch {
    if (!getWorkspaceAuthSession()) return
    const ok = await tryRefreshSession(client)
    if (!ok) forceSessionExpired()
  }
}

/**
 * Proactively refreshes the access token before expiry and when the tab regains focus.
 * Also validates that a persisted login still matches a live server session.
 */
export function SessionRefreshWatcher() {
  const client = useHttpClient()

  useEffect(() => {
    if (!isAuthenticated()) return

    let refreshTimer: ReturnType<typeof setTimeout> | undefined
    let cancelled = false

    const scheduleRefresh = () => {
      if (refreshTimer) clearTimeout(refreshTimer)
      const session = getWorkspaceAuthSession()
      if (!session) return

      const expiresAtMs = readAccessTokenExpiryMs(session.accessToken)
      if (expiresAtMs == null) return

      const delay = Math.max(expiresAtMs - Date.now() - REFRESH_BEFORE_EXPIRY_MS, 0)
      refreshTimer = setTimeout(() => {
        void refreshIfNeeded(client)
      }, delay)
    }

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      void ensureLiveSession(client).then(() => {
        if (!cancelled) scheduleRefresh()
      })
    }

    void ensureLiveSession(client).then(() => {
      if (!cancelled) scheduleRefresh()
    })
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      cancelled = true
      if (refreshTimer) clearTimeout(refreshTimer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [client])

  return null
}
